/**
 * Aviso de instalación de la PWA.
 *
 * QUÉ HACE
 * ---------------------------------------------------------------------------
 * El CMDB ya es instalable (manifest + service worker), pero instalar una web
 * no es algo que el usuario medio descubra solo: hay que enseñarle el camino.
 * Este componente muestra una banda discreta en la parte inferior:
 *
 *   · Android / Chrome / Edge → botón «Instalar» que lanza el diálogo nativo
 *     (`beforeinstallprompt`). El evento se captura y se guarda: si se dejara
 *     pasar, el navegador enseñaría su propio aviso en cualquier momento.
 *   · iPhone / iPad → instrucciones (Compartir → Añadir a pantalla de inicio),
 *     porque iOS no permite lanzar la instalación desde la página.
 *
 * CUÁNDO NO SALE
 * ---------------------------------------------------------------------------
 *  · Si ya está abierta como aplicación instalada (`display-mode: standalone`).
 *  · Si el usuario le dio a «Ahora no» (se recuerda 60 días).
 *  · En escritorio con ancho grande: en una pantalla de 27" el aviso sobra;
 *     ahí el icono de instalar del navegador es suficiente y menos invasivo.
 */

import React, { useEffect, useState } from 'react';
import './InstallAppPrompt.css';

const CLAVE_DESCARTE = 'cmdb.pwa.install.dismissedAt';
const DIAS_RECORDAR = 60;

function esStandalone() {
  if (typeof window === 'undefined') return true;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches
    || window.navigator.standalone === true
  );
}

function esIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ se anuncia como Mac con pantalla táctil.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function descartadoHacePoco() {
  try {
    const valor = Number(localStorage.getItem(CLAVE_DESCARTE) || 0);
    if (!valor) return false;
    return Date.now() - valor < DIAS_RECORDAR * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallAppPrompt() {
  const [evento, setEvento] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const [instalando, setInstalando] = useState(false);

  useEffect(() => {
    if (esStandalone() || descartadoHacePoco()) return undefined;

    const onPrompt = (e) => {
      // Sin `preventDefault` el navegador decide cuándo enseñar su aviso.
      e.preventDefault();
      setEvento(e);
      setVisible(true);
    };
    const onInstalado = () => {
      setEvento(null);
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalado);

    // iOS no emite `beforeinstallprompt`: se explica el gesto manual, pero
    // solo en pantalla pequeña (en un Mac no tiene sentido).
    const esPantallaPequena = window.matchMedia?.('(max-width: 900px)')?.matches;
    if (esIOS() && esPantallaPequena) {
      setIos(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalado);
    };
  }, []);

  const cerrar = () => {
    setVisible(false);
    try { localStorage.setItem(CLAVE_DESCARTE, String(Date.now())); } catch { /* modo privado */ }
  };

  const instalar = async () => {
    if (!evento) return;
    setInstalando(true);
    try {
      await evento.prompt();
      const eleccion = await evento.userChoice;
      if (eleccion?.outcome === 'accepted') {
        setVisible(false);
      } else {
        cerrar();
      }
    } catch {
      cerrar();
    } finally {
      setEvento(null);
      setInstalando(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="pwa-install" role="region" aria-label="Instalar la aplicación">
      <div className="pwa-install-icon" aria-hidden="true">
        {/* Mismo dibujo que el icono de la PWA: un rack visto de frente. */}
        <svg viewBox="0 0 48 48" fill="none">
          <rect x="10" y="7" width="28" height="34" rx="5" stroke="currentColor" strokeWidth="2.4" />
          <path d="M15 16h18M15 24h18M15 32h18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="19" cy="16" r="1.6" fill="currentColor" />
          <circle cx="19" cy="24" r="1.6" fill="currentColor" />
          <circle cx="19" cy="32" r="1.6" fill="currentColor" />
        </svg>
      </div>

      <div className="pwa-install-text">
        <strong>Instala el CMDB</strong>
        {ios ? (
          <span>
            Toca <b>Compartir</b> y luego <b>Añadir a pantalla de inicio</b> para
            abrirla como una app, a pantalla completa.
          </span>
        ) : (
          <span>
            Se abre a pantalla completa desde el escritorio, sin barra de
            direcciones, y arranca al instante.
          </span>
        )}
      </div>

      <div className="pwa-install-actions">
        {!ios && (
          <button
            type="button"
            className="pwa-install-btn"
            onClick={instalar}
            disabled={instalando || !evento}
          >
            {instalando ? 'Instalando…' : 'Instalar'}
          </button>
        )}
        <button type="button" className="pwa-install-later" onClick={cerrar}>
          Ahora no
        </button>
      </div>
    </div>
  );
}
